import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LANGUAGES } from "@/i18n/translations";
import { useLang } from "@/context/LangContext";
import { api, formatApiError, mediaUrl } from "@/lib/api";

export const MultiLangField = ({ label, value = {}, onChange, multiline, testPrefix }) => (
  <div className="space-y-3">
    {label && <Label className="text-sm font-bold">{label}</Label>}
    <div className="grid gap-3 md:grid-cols-3">
      {LANGUAGES.map((l) => {
        const props = {
          value: value[l.code] || "",
          onChange: (e) => onChange({ ...value, [l.code]: e.target.value }),
          "data-testid": `${testPrefix}-${l.code}-input`,
        };
        return (
          <div key={l.code} className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{l.label}</span>
            {multiline ? <Textarea rows={4} {...props} /> : <Input {...props} />}
          </div>
        );
      })}
    </div>
  </div>
);

export const MediaField = ({ label, value, onChange, testPrefix }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      onChange((await api.post("/admin/media", fd)).data.url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-border p-5 sm:flex-row sm:items-center">
      <div className="flex h-28 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted sm:w-40">
        {value ? <img src={mediaUrl(value)} alt={label} className="h-full w-full object-cover" data-testid={`${testPrefix}-preview`} /> : <Upload className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 space-y-3">
        <Label className="text-sm font-bold">{label}</Label>
        <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testPrefix}-file-input`} onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testPrefix}-upload-button`} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? t("uploading") : t("upload")}
        </button>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={t("image_url")} data-testid={`${testPrefix}-url-input`} />
      </div>
    </div>
  );
};
